import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import Cars from "@/pages/Cars";
import { routeHead } from "@/lib/seo/route-head";
import { brandOnlyTarget } from "@/utils/carsBrandRedirect";
import { paginatedListingsQueryOptions } from "@/hooks/usePaginatedListings";
import { classifyListSearch, filteredHead, paginatedHead, trackingParams } from "@/utils/listSearchParams";

const PAGE_SIZE = 10;

export const Route = createFileRoute("/cars/")({
  validateSearch: (search: Record<string, unknown>) =>
    Object.fromEntries(
      Object.entries(search)
        .map(([k, v]) => [k, typeof v === "number" ? String(v) : v])
        .filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    ),
  // ?marca=BMW / ?brand=bmw / ?make=BMW with no other filter is the same
  // result set as the indexable brand page, so send it there with a 301.
  // ?page=1 is the clean /cars; an invalid page value is a 404.
  // Server-only: in-app filter changes keep the visitor inside the /cars UI.
  beforeLoad: ({ search }) => {
    if (typeof window !== "undefined") return;
    const target = brandOnlyTarget(search as Record<string, unknown>);
    if (target) throw redirect({ href: target, statusCode: 301 });
    const kind = classifyListSearch(search as Record<string, unknown>);
    if (kind.kind === "invalid-page") throw notFound();
    if (kind.kind === "page-one") {
      const t = trackingParams(search as Record<string, unknown>).toString();
      throw redirect({ href: `/cars${t ? `?${t}` : ""}`, statusCode: 301 });
    }
  },
  head: ({ match }) => {
    const base = routeHead("/cars/");
    const kind = classifyListSearch((match?.search ?? {}) as Record<string, unknown>);
    if (kind.kind === "page") return paginatedHead(base, "/cars", kind.page);
    if (kind.kind === "filtered") return filteredHead(base);
    return base;
  },
  // Server-render the requested page of the unfiltered list (page 1 or ?page=N)
  // so its listings are in the initial HTML. A page past the last one is a 404.
  loader: async ({ context, location }) => {
    if (typeof window !== "undefined") return;
    const kind = classifyListSearch(location.search as Record<string, unknown>);
    if (kind.kind !== "clean" && kind.kind !== "page") return;
    const data = (await context.queryClient
      .ensureInfiniteQueryData(
        paginatedListingsQueryOptions({
          category_code: "cars",
          country_code: "RO",
          pageSize: PAGE_SIZE,
          page: kind.page,
        }) as any,
      )
      .catch(() => undefined)) as { pages?: Array<{ totalCount?: number; listings?: unknown[] }> } | undefined;
    const first = data?.pages?.[0];
    if (kind.kind === "page" && first && (first.listings?.length ?? 0) === 0) throw notFound();
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="surface-light min-h-screen"><Cars /></div>
  );
}
